import React, { useState, useEffect, useCallback, useMemo } from 'react';
import moment from 'moment';
import { withApi, useResponsive } from 'utils';
import { datadogLogs } from '@datadog/browser-logs';
import { datadogRum } from '@datadog/browser-rum';

import {
  Dialog,
  DIALOG_TYPES,
  Table,
  Button,
  Spinner,
  showErrorAlert,
  Icon,
} from '../../../alf-design';
import { download } from 'assets/icon';

const documentId = (document) => document.Id;
const SORT_DIRECTIONS = {
  ASC: 'asc',
  DESC: 'desc',
};

const isInvoiceFile = (document) =>
  String(document.Title ?? '')
    .toLowerCase()
    .replace(/\b\w/g, (character) => character.toUpperCase()) === 'Invoice';

const capitalizeDocumentTitle = (title, policyNumber, isFusion) => {
  const docTitle = String(title ?? '')
    .toLowerCase()
    .replace(/\b\w/g, (character) => character.toUpperCase());

  if (isFusion && docTitle !== 'Invoice') {
    const product = String(policyNumber).endsWith('N')
      ? 'Homeowner'
      : 'Hurricane';
    return `${product} - ${docTitle}`;
  }
  return docTitle;
};

const parseDocumentDate = (date) =>
  moment(
    date,
    [
      'M/D/YYYY',
      'MM/DD/YYYY',
      'M/D/YYYY h:mm:ss A',
      'MM/DD/YYYY h:mm:ss A',
      moment.ISO_8601,
    ],
    true,
  );

const getDocumentDateValue = (document) => {
  const date = parseDocumentDate(document.Date);

  return date.isValid() ? date.valueOf() : 0;
};

const formatDocumentDate = (date) => {
  const documentDate = parseDocumentDate(date);

  return documentDate.isValid() ? documentDate.format('l') : String(date || '');
};

const formatPolicyPeriod = (policyPeriod) => {
  if (policyPeriod == null || policyPeriod === '') {
    return '';
  }

  return String(policyPeriod).replace(/(\S)-(\S)/g, '$1 - $2');
};

const sortDataByDate = (array, direction = SORT_DIRECTIONS.DESC) => {
  const directionModifier = direction === SORT_DIRECTIONS.ASC ? 1 : -1;

  return [...array].sort(
    (a, b) =>
      (getDocumentDateValue(a) - getDocumentDateValue(b)) * directionModifier,
  );
};

const PolicyDownloads = ({
  show,
  onClose,
  policyNumber,
  policyFusionNH,
  policySystem,
  isFusion,
  api,
}) => {
  const [downloading, setDownloading] = useState(false);
  const [loading, setLoading] = useState(false);
  const [documents, setDocuments] = useState([]);
  const [selectedDocument, setSelectedDocument] = useState('');
  const [sortDirection, setSortDirection] = useState(SORT_DIRECTIONS.DESC);

  const { screenIsDown, height } = useResponsive();

  const loadDocuments = useCallback(
    (policyNumber) => {
      setLoading(true);
      api.withAuth
        .getPolicyDeclaration(policyNumber)
        .then((res) => {
          if (res.data) {
            const { data: docs } = res;
            const newDocs = docs.map((item) => ({
              ...item,
              PolicyNumber: policyNumber,
            }));
            setDocuments((prevDocs) => {
              const skipNHInvoice = isFusion && prevDocs.some(isInvoiceFile);

              return [
                ...prevDocs,
                ...(skipNHInvoice
                  ? newDocs.filter((doc) => !isInvoiceFile(doc))
                  : newDocs),
              ];
            });
          }
          setLoading(false);
        })
        .catch((error) => {
          setLoading(false);
          let description;
          if (error.response) {
            description = error.response.data.error;
          } else if (error.request) {
            description =
              'Network error. Please check your connection and try again.';
          } else {
            description = `Error attempting to load documents.`;
          }
          datadogLogs.logger.error(`Load Documents - ${policyNumber}`, {
            error,
          });
          showErrorAlert({
            message: 'Policy Declaration Error',
            description,
          });
        });
    },
    [api, isFusion],
  );

  useEffect(() => {
    if (show) {
      setDocuments([]);
      setSortDirection(SORT_DIRECTIONS.DESC);
      loadDocuments(policyNumber);
      if (isFusion) loadDocuments(policyFusionNH);
    } else {
      setSortDirection(SORT_DIRECTIONS.DESC);
    }
  }, [loadDocuments, show]);

  const sortedDocuments = useMemo(
    () => sortDataByDate(documents, sortDirection),
    [documents, sortDirection],
  );

  const downloadDocument = async (document) => {
    try {
      setSelectedDocument(`${document.PolicyNumber}-${document.Id}`);
      setDownloading(true);
      await api.withAuth.getPolicyDocument(document.PolicyNumber, {
        id: document.Id,
        title: document.Title,
        source: document.Source ?? 'policy',
      });
      datadogRum.addAction('document.download.succeeded', {
        document_source: document.Source ?? 'policy',
      });
      setDownloading(false);
      setSelectedDocument('');
    } catch (err) {
      datadogRum.addError(err, {
        event_name: 'document.download.failed',
        document_source: document.Source ?? 'policy',
        http_status: err?.response?.status,
      });
      setDownloading(false);
      setSelectedDocument('');
      let description;
      if (err.response) {
        description = err.response.data.error;
      } else if (err.request) {
        description =
          'Network error. Please check your connection and try again.';
      } else {
        description = `Error attempting to fetch your documents. Refresh page to try again.`;
      }

      datadogLogs.logger.error(`Download Document - ${policyNumber}`, {
        err,
      });
      showErrorAlert({
        message: 'Policy Error',
        description: description,
      });
    }
  };

  const renderIcon = (val) => {
    if (downloading && selectedDocument === `${val.PolicyNumber}-${val.Id}`) {
      return <Spinner />;
    }
    return (
      <Button
        className="m-auto"
        type="icon"
        ariaLabel="Download File"
        id={`downloadFile-${val.PolicyNumber}-${val.Id}`}
        data-dd-action-name="dashboard.policy_documents.download"
        onClick={() => downloadDocument(val)}
        icon={
          <img
            alt=""
            name="fileDownload"
            src={download}
            className="h-[20px] w-[20px]"
          />
        }
        disabled={downloading}
      />
    );
  };

  const toggleCreatedDateSort = () => {
    setSortDirection((currentDirection) =>
      currentDirection === SORT_DIRECTIONS.DESC
        ? SORT_DIRECTIONS.ASC
        : SORT_DIRECTIONS.DESC,
    );
  };

  const sortIconName =
    sortDirection === SORT_DIRECTIONS.DESC
      ? 'MdArrowDownward'
      : 'MdArrowUpward';

  const createdDateHeader = (
    <button
      type="button"
      className="flex cursor-pointer items-center justify-center whitespace-nowrap border-0 bg-transparent p-0 text-center text-legacy-textBody hover:text-legacy-primary"
      onClick={toggleCreatedDateSort}
      aria-label={`Sort by created date ${
        sortDirection === SORT_DIRECTIONS.DESC ? 'ascending' : 'descending'
      }`}
    >
      <span className="px-[5px]">Created Date</span>
      <Icon
        name={sortIconName}
        fontSize={16}
        color="currentColor"
        ariaHidden={true}
      />
    </button>
  );

  const policyPeriodHeader = (
    <span className="block w-full text-center whitespace-nowra">
      Policy Period
    </span>
  );

  const mobileDownloadHeader = (
    <span className="block w-full whitespace-nowrap text-center">Download</span>
  );

  const renderMobilePolicyPeriod = (val) => (
    <div className="w-full">{formatPolicyPeriod(val.PolicyPeriod)}</div>
  );

  const isMobile = screenIsDown('md');
  const isIPXPolicy = policySystem === 'IPX';
  const tableMaxHeight = screenIsDown('md') ? height - 200 : height / 2;
  const documentColumn = {
    title: 'Document',
    key: 'document',
    headerStyle: {
      width: '160px',
      minWidth: '160px',
      maxWidth: '160px',
      textAlign: 'center',
    },
    cellClassName: 'w-[160px] min-w-[160px] max-w-[160px] text-center',
    render: (val) =>
      capitalizeDocumentTitle(val.Title, val.PolicyNumber, isFusion),
  };

  const createdDateColumn = {
    title: createdDateHeader,
    key: 'createdDate',
    headerStyle: {
      width: '160px',
      minWidth: '160px',
      maxWidth: '160px',
      textAlign: 'center',
      justifyItems: 'center',
    },
    cellClassName: 'w-[160px] min-w-[160px] max-w-[160px] text-center',
    render: (val) => formatDocumentDate(val.Date),
  };

  const policyPeriodColumn = {
    title: policyPeriodHeader,
    key: 'policyPeriod',
    headerStyle: {
      width: '180px',
      minWidth: '180px',
      maxWidth: '180px',
    },
    cellClassName: 'w-[180px] min-w-[180px] max-w-[180px] text-center',
    render: isMobile
      ? renderMobilePolicyPeriod
      : (val) => formatPolicyPeriod(val.PolicyPeriod),
  };

  const downloadColumn = {
    title: isMobile ? mobileDownloadHeader : 'Download',
    key: 'downloadButton',
    headerStyle: {
      width: '100px',
      minWidth: '100px',
      maxWidth: '100px',
      textAlign: 'center',
    },
    cellClassName: 'w-[100px] min-w-[100px] max-w-[100px] text-center',
    render: renderIcon,
  };

  const columns = [
    documentColumn,
    ...(isMobile ? [createdDateColumn] : []),
    ...(isIPXPolicy ? [policyPeriodColumn] : []),
    ...(!isMobile ? [createdDateColumn] : []),
    downloadColumn,
  ];

  return (
    <Dialog
      id="policy-downloads"
      type={DIALOG_TYPES.CONTENT}
      invokingElementId="download-policy"
      show={show}
      onClose={onClose}
      title="Policy Documents"
      subtitle="View and download your policy documents."
      ariaLabel="Policy documents"
      contentClassName="!flex !overflow-y-hidden"
    >
      <div className="flex">
        <Table
          data={sortedDocuments}
          columns={columns}
          loading={loading}
          dataRowId={documentId}
          disableSort
          maxHeight={tableMaxHeight}
        />
      </div>
    </Dialog>
  );
};

export default withApi(PolicyDownloads);
